import {
  ForwardedRef,
  KeyboardEventHandler,
  forwardRef,
  useRef,
  useState
} from "react";
import cx from "classnames";
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";

import { Icon } from "./Icon";

interface PasswordInputProps {
  name: string;
  value?: string;
  onChangeValue: (value: string) => void;
  disabled?: boolean;
  className?: string;
  errorMessage?: string;
  dataTestId?: string;
  placeholder?: string;
  showToggle?: boolean;
  autoComplete?: string;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  /**
   * Show the error border without rendering a message — for when the error is
   * surfaced elsewhere (e.g. a shared error block above the field). Use
   * `errorMessage` instead when this field owns its own message.
   */
  hasError?: boolean;
}

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      name,
      value = "",
      onChangeValue,
      className,
      errorMessage,
      disabled,
      dataTestId,
      placeholder = "••••••••",
      showToggle = true,
      autoComplete,
      onKeyDown,
      hasError = false
    }: PasswordInputProps,
    forwardedRef: ForwardedRef<HTMLInputElement>
  ) => {
    const ownRef = useRef<HTMLInputElement>(null);
    const ref = forwardedRef || ownRef;
    const [isVisible, setIsVisible] = useState(false);

    const toggleVisibility = () => {
      setIsVisible(!isVisible);
    };

    // Determine if we should use legacy Input class styling or modern Tailwind styling
    const useLegacyStyling = className === "Input";
    const showError = !!errorMessage || hasError;

    return (
      <>
        <div className={cx(
          useLegacyStyling ? "relative" : "relative rounded-md shadow-sm",
          !useLegacyStyling && className
        )}>
          <input
            ref={ref}
            type={isVisible ? "text" : "password"}
            name={name}
            id={name}
            data-testid={dataTestId}
            disabled={disabled}
            autoComplete={autoComplete}
            placeholder={placeholder}
            className={cx(
              useLegacyStyling ? "Input" : (
                "form-input block rounded-md border-0 py-1.5 text-gray-900 " +
                "ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 " +
                "focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              ),
              showToggle && (useLegacyStyling ? "pr-10" : "pr-10"),
              showError && useLegacyStyling && "has-error",
              showError && !useLegacyStyling &&
                "text-red-900 ring-red-300 placeholder:text-red-300 focus:ring-red-500"
            )}
            value={value}
            onChange={e => onChangeValue(e.target.value)}
            onKeyDown={onKeyDown}
            aria-invalid={showError}
            aria-describedby={`${name}-error`}
          />
          {showToggle && (
            <div className="absolute inset-y-0 right-0 flex items-center">
              <button
                type="button"
                className="h-full px-3 bg-transparent border-0 cursor-pointer text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
                onClick={toggleVisibility}
                disabled={disabled}
                aria-label={isVisible ? "Hide password" : "Show password"}
              >
                <Icon name={isVisible ? "eyeOff" : "eye"} width="20px" />
              </button>
            </div>
          )}
          {errorMessage && (
            <div className={cx(
              "pointer-events-none absolute inset-y-0 right-0 flex items-center",
              showToggle ? "pr-12" : "pr-3"
            )}>
              <ExclamationCircleIcon
                className="h-5 w-5 text-red-500"
                aria-hidden="true"
              />
            </div>
          )}
        </div>
        {errorMessage && (
          <p className="mt-2 text-sm text-red-600" id={`${name}-error`}>
            {errorMessage}
          </p>
        )}
      </>
    );
  }
);

PasswordInput.displayName = "PasswordInput";

export default PasswordInput;